import { useState } from 'react';
import { cx } from '../../lib/cx.js';
import { formatCount, shortDate } from '../../lib/format.js';
import { useMeasure } from '../../lib/useMeasure.js';

export interface TrendPoint {
  /** ISO date (YYYY-MM-DD) or timestamp. */
  date: string;
  values: number[];
}

export interface TrendSeries {
  label: string;
  /** A chart token reference such as `var(--chart-1)`. */
  color: string;
}

export interface TrendChartProps {
  points: TrendPoint[];
  series: TrendSeries[];
  height?: number;
  /** Named in the title, so a single series needs no legend box. */
  caption?: string;
  className?: string;
  emptyLabel?: string;
}

const PAD = { top: 12, right: 16, bottom: 22, left: 40 };

/**
 * Line (plus a 10% area wash for a single series) over time, with a crosshair
 * and tooltip. 2px lines, hairline solid gridlines, recessive axes, direct end
 * label on the last point; a legend appears only from two series up.
 */
export function TrendChart({
  points,
  series,
  height = 200,
  caption,
  className,
  emptyLabel = 'No data in this period',
}: TrendChartProps) {
  const [wrapRef, width] = useMeasure<HTMLDivElement>(720);
  const [hover, setHover] = useState<number | null>(null);

  if (points.length === 0 || series.length === 0) {
    return (
      <div
        ref={wrapRef}
        className={cx(
          'flex items-center justify-center rounded-[var(--radius-md)] border border-dashed border-[var(--border)] text-[var(--text-base)] text-[var(--text-3)]',
          className,
        )}
        style={{ height }}
      >
        {emptyLabel}
      </div>
    );
  }

  const innerW = Math.max(120, width - PAD.left - PAD.right);
  const innerH = height - PAD.top - PAD.bottom;
  const maxValue = Math.max(
    1,
    ...points.flatMap((p) => p.values.map((v) => (Number.isFinite(v) ? v : 0))),
  );
  const ticks = niceTicks(maxValue);
  const top = ticks[ticks.length - 1] ?? maxValue;

  const x = (i: number): number =>
    PAD.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = (v: number): number => PAD.top + innerH - (Math.max(0, v) / top) * innerH;

  const hovered = hover !== null ? points[hover] : undefined;

  return (
    <div ref={wrapRef} className={cx('relative w-full', className)}>
      <svg
        width={width}
        height={height}
        role="img"
        aria-label={caption ?? 'Trend'}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          const local = event.clientX - rect.left - PAD.left;
          const step = points.length > 1 ? innerW / (points.length - 1) : innerW;
          const index = Math.round(local / step);
          setHover(Math.min(points.length - 1, Math.max(0, index)));
        }}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={PAD.left}
              x2={PAD.left + innerW}
              y1={y(tick)}
              y2={y(tick)}
              stroke="var(--chart-grid)"
              strokeWidth={1}
            />
            <text
              x={PAD.left - 6}
              y={y(tick) + 3}
              textAnchor="end"
              className="tnum"
              fontSize={10}
              fill="var(--chart-ink-muted)"
            >
              {formatCount(tick)}
            </text>
          </g>
        ))}

        {series.length === 1 && (
          <path
            d={`${linePath(points, 0, x, y)} L ${x(points.length - 1)} ${y(0)} L ${x(0)} ${y(0)} Z`}
            fill={series[0]!.color}
            fillOpacity={0.1}
          />
        )}

        {series.map((s, si) => (
          <path
            key={s.label}
            d={linePath(points, si, x, y)}
            fill="none"
            stroke={s.color}
            strokeWidth={2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}

        {points.length > 0 &&
          series.map((s, si) => (
            <circle
              key={`end-${s.label}`}
              cx={x(points.length - 1)}
              cy={y(points[points.length - 1]!.values[si] ?? 0)}
              r={4}
              fill={s.color}
              stroke="var(--surface)"
              strokeWidth={2}
            />
          ))}

        {hover !== null && hovered && (
          <>
            <line
              x1={x(hover)}
              x2={x(hover)}
              y1={PAD.top}
              y2={PAD.top + innerH}
              stroke="var(--chart-axis)"
              strokeWidth={1}
            />
            {series.map((s, si) => (
              <circle
                key={`hover-${s.label}`}
                cx={x(hover)}
                cy={y(hovered.values[si] ?? 0)}
                r={4}
                fill={s.color}
                stroke="var(--surface)"
                strokeWidth={2}
              />
            ))}
          </>
        )}

        <line
          x1={PAD.left}
          x2={PAD.left + innerW}
          y1={PAD.top + innerH}
          y2={PAD.top + innerH}
          stroke="var(--chart-axis)"
          strokeWidth={1}
        />

        {axisLabelIndices(points.length).map((i) => (
          <text
            key={i}
            x={x(i)}
            y={height - 6}
            textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
            className="tnum"
            fontSize={10}
            fill="var(--chart-ink-muted)"
          >
            {shortDate(points[i]!.date)}
          </text>
        ))}
      </svg>

      {series.length > 1 && (
        <ul className="mt-1 flex flex-wrap items-center gap-3 text-[var(--text-sm)] text-[var(--text-2)]">
          {series.map((s) => (
            <li key={s.label} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden
                className="inline-block h-[2px] w-3 rounded-full"
                style={{ backgroundColor: s.color }}
              />
              {s.label}
            </li>
          ))}
        </ul>
      )}

      {hovered && (
        <div
          role="status"
          className="pointer-events-none absolute top-1 z-10 min-w-[120px] -translate-x-1/2 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] px-2 py-1 text-[var(--text-sm)] shadow-[var(--shadow-2)]"
          style={{ left: Math.min(Math.max(x(hover ?? 0), 70), width - 70) }}
        >
          <p className="font-medium text-[var(--text)]">{shortDate(hovered.date)}</p>
          {series.map((s, si) => (
            <p key={s.label} className="flex items-center gap-1.5 text-[var(--text-2)]">
              <span
                aria-hidden
                className="inline-block h-[6px] w-[6px] rounded-full"
                style={{ backgroundColor: s.color }}
              />
              {s.label}
              <span className="tnum ml-auto font-medium text-[var(--text)]">
                {formatCount(hovered.values[si] ?? 0)}
              </span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function linePath(
  points: TrendPoint[],
  seriesIndex: number,
  x: (i: number) => number,
  y: (v: number) => number,
): string {
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.values[seriesIndex] ?? 0)}`)
    .join(' ');
}

/** Clean axis steps (0 / 5 / 10 …) that bracket the maximum. */
function niceTicks(max: number, count = 4): number[] {
  const rough = max / count;
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.max(rough, 1))));
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ?? magnitude * 10;
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.999; v += step) out.push(Math.round(v));
  return out;
}

function axisLabelIndices(length: number): number[] {
  if (length <= 1) return [0];
  if (length <= 4) return Array.from({ length }, (_, i) => i);
  return [0, Math.floor(length / 3), Math.floor((2 * length) / 3), length - 1];
}
